import { PayloadAction } from '@reduxjs/toolkit';
import { put, takeLatest } from 'redux-saga/effects';
import { ProjectId, UserId } from 'api/models';
import { ProjectAPI } from 'api/project';
import { InvitedMembers, addUsers } from 'data/members/slice';
import {
  CreateAndInviteProjectMembers,
  createAndInviteProjectMembersAction,
} from 'data/projects/actions';
import { getProjectMembersQueryKey, queryClient } from 'queries';
import {
  MEMBERS_V2_MODIFIED_QUERY_KEY,
  MEMBERS_V2_QUERY_KEY,
} from 'queries/members';
import { add } from 'stores/memberships';
import { addedAll } from 'stores/users';

interface Callback {
  (data: any): void;
}

function* createAndInviteProjectMembers(
  action: PayloadAction<{
    projectId: number;
    data: (CreateAndInviteProjectMembers | InvitedMembers)[];
    handleSuccess?: Callback;
    handleFailure?: Callback;
  }>,
) {
  const { payload } = action;
  const { data, projectId, handleSuccess, handleFailure } = payload;

  try {
    const newUsers = (data as CreateAndInviteProjectMembers[]).filter(
      ({ user }) => user.emailId != null,
    ) as CreateAndInviteProjectMembers[];
    const existingUsers = (data as InvitedMembers[]).filter(
      ({ user }) => user.userId != null,
    ) as InvitedMembers[];

    const createdAndInvitedResponse =
      newUsers.length > 0
        ? yield ProjectAPI.createAndInviteProjectMembers(projectId, newUsers)
        : [];
    const invitedResponse =
      existingUsers.length > 0
        ? yield ProjectAPI.addMembers(projectId, existingUsers)
        : [];

    if (handleSuccess) {
      handleSuccess(payload);
    }

    yield put(addUsers(createdAndInvitedResponse.map(({ user }) => user)));
    yield put(addedAll(createdAndInvitedResponse.map(({ user }) => user)));

    yield put(
      add({
        projectId: ProjectId(projectId),
        memberships: createdAndInvitedResponse
          .concat(invitedResponse)
          .map(
            ({
              user: { userId },
              permissionSet: { permissionSetType: memberType },
              joinedAt,
            }) => ({ userId: UserId(userId), memberType, joinedAt }),
          ),
      }),
    );
    yield queryClient.refetchQueries(
      getProjectMembersQueryKey(ProjectId(projectId)),
    );
    yield queryClient.refetchQueries(MEMBERS_V2_QUERY_KEY);
    yield queryClient.refetchQueries(MEMBERS_V2_MODIFIED_QUERY_KEY);
  } catch (error) {
    if (handleFailure) {
      handleFailure(error);
    }
  }
}

export const watchCreateAndInviteProjectMembers = function* (): Generator {
  yield takeLatest(
    createAndInviteProjectMembersAction,
    createAndInviteProjectMembers,
  );
};
